// Picker: the admin's dropdown for choosing one value: a field-styled
// trigger and a popover list (search when long, groups, a description per
// option, a check on the chosen one). Keyboard: arrows, Enter, Escape,
// type to search.
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cx } from "./ui";

export type PickerOption = { value: string; label: ReactNode; text?: string; description?: ReactNode; group?: string; mono?: boolean };

export function Picker({ value, options, onChange, placeholder, searchPlaceholder, disabled, groupLabel, id, className, invalid }: {
  value: string; options: PickerOption[]; onChange: (v: string) => void; placeholder?: ReactNode; searchPlaceholder?: string;
  disabled?: boolean; groupLabel?: (g: string) => ReactNode; id?: string; className?: string; invalid?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const searchable = options.length > 8;
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => `${o.text ?? (typeof o.label === "string" ? o.label : "")} ${o.value} ${o.group ?? ""}`.toLowerCase().includes(needle));
  }, [options, q]);
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  useEffect(() => {
    if (open) {
      setQ("");
      setActive(Math.max(0, options.findIndex((o) => o.value === value)));
    }
  }, [open, options, value]);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const choose = (o: PickerOption | undefined) => {
    if (!o) return;
    onChange(o.value);
    setOpen(false);
    button.current?.focus();
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) { e.preventDefault(); setOpen(true); }
      return;
    }
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); button.current?.focus(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, shown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(shown[active]); }
    else if (e.key === "Tab") setOpen(false);
  };

  let lastGroup: string | undefined;
  return (
    <div ref={root} className={cx("relative", className)} onKeyDown={onKey}>
      <button ref={button} id={id} type="button" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined}
        onClick={() => setOpen(!open)}
        className={cx("field flex items-center justify-between gap-2 text-start", open && "border-brand ring-2 ring-brand/40", invalid && "border-danger")}>
        <span className={cx("min-w-0 truncate", !current && "text-ink-subtle", current?.mono && "font-mono text-[13px]")} dir={current?.mono ? "ltr" : undefined}>
          {current ? current.label : placeholder}
        </span>
        <ChevronDown className={cx("size-4 shrink-0 text-ink-muted transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="anim-pop absolute inset-x-0 z-40 mt-1.5 min-w-64 overflow-hidden rounded-xl border border-line bg-white text-ink shadow-pop">
          {searchable && (
            <div className="flex items-center gap-2 border-b border-line px-3">
              <Search className="size-4 shrink-0 text-ink-subtle" />
              <input autoFocus value={q} placeholder={searchPlaceholder} aria-controls={listId}
                onChange={(e) => { setQ(e.target.value); setActive(0); }}
                className="h-10 w-full border-0 bg-transparent text-sm outline-none !ring-0 !ring-offset-0 placeholder:text-ink-subtle" />
            </div>
          )}
          <ul ref={list} id={listId} role="listbox" className="max-h-72 overflow-auto p-1.5">
            {shown.map((o, i) => {
              const header = o.group && o.group !== lastGroup ? o.group : undefined;
              lastGroup = o.group;
              const selected = o.value === value;
              return (
                <li key={o.value} role="presentation">
                  {header && <div className="px-2.5 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle first:pt-1">{groupLabel ? groupLabel(header) : header}</div>}
                  <div role="option" aria-selected={selected} data-index={i} onMouseEnter={() => setActive(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(o)}
                    className={cx("flex cursor-pointer items-start gap-2 rounded-lg px-2.5 py-2 text-sm", i === active && "bg-subtle", selected && "text-brand")}>
                    <span className="min-w-0 flex-1">
                      <span className={cx("block truncate", selected && "font-medium", o.mono && "font-mono text-[13px]")} dir={o.mono ? "ltr" : undefined}>{o.label}</span>
                      {o.description && <span className="mt-0.5 block text-xs text-ink-muted">{o.description}</span>}
                    </span>
                    {selected && <Check className="mt-0.5 size-4 shrink-0" />}
                  </div>
                </li>
              );
            })}
            {shown.length === 0 && <li className="px-2.5 py-3 text-sm text-ink-muted">—</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// Segmented: a small set of exclusive choices as a pill (access levels,
// modes). A radio group for assistive tech.
export function Segmented<T extends string>({ value, options, onChange, label, disabled, size = "md" }: {
  value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void; label: string; disabled?: boolean; size?: "sm" | "md";
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cx("inline-flex shrink-0 rounded-full bg-muted p-0.5", disabled && "opacity-60")}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={on} disabled={disabled} onClick={() => onChange(o.value)}
            className={cx("whitespace-nowrap rounded-full font-medium transition-colors disabled:cursor-not-allowed",
              size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3.5 text-[13px]",
              on ? "bg-white text-ink shadow-card ring-1 ring-line" : "text-ink-muted hover:text-ink")}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
